import React, { useCallback, useMemo, useState } from 'react'
import { Button, LinkButton } from '@/atoms/Button'
import { PayItForwardIcon } from '@/atoms/Icons/PayItForwardIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { ParagraphMD, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { TheatricalReleaseObject } from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { projectThemes } from '@/utils/project-themes'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPifEmailCollectionModal } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifEmailCollectionModal'

interface Props {
  theatricalRelease: TheatricalReleaseObject
  togglePifSection?: () => void
}

export type ModalType = 'ticket' | 'free-ticket'
export type ModalStateType = 'email' | 'success'
export const TheatricalPifCtaSection: React.FC<Props> = ({ togglePifSection, theatricalRelease }) => {
  const { t } = useTranslate('pif')
  const track = useSafeTrack()
  const { push, query } = useSafeRouter()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [modalType, setModalType] = useState<ModalType>('ticket')
  const [modalState, setModalState] = useState<ModalStateType>('email')

  const ticketUrl = paths.tickets.checkout.showtimes(theatricalRelease?.theatricalSlug)

  const openModal = useCallback((modalType: ModalType) => {
    if (modalType === 'ticket') {
      setModalType('ticket')
    } else {
      setModalType('free-ticket')
    }
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const purchaseTicketDescription = useMemo(() => {
    const slug = query?.slug as string
    if (slug === slugs.soundOfFreedom) {
      return t('SofHelpTicketsDescription', 'Buy tickets now to show your support of ending child trafficking.')
    }
    return t('projectTicketsDescription', 'Buy tickets now to show your support for {{projectName}}', {
      projectName: projectThemes[slug].name,
    })
  }, [query?.slug, t])

  const handleGetShowtimesClick = useCallback(() => {
    track('Buy Tickets Clicked from theatrical PIF Page')
    if (theatricalRelease?.region?.ticketsStatus === 'COMING_SOON') return openModal('ticket')
    push(ticketUrl)
  }, [openModal, theatricalRelease?.region?.ticketsStatus, ticketUrl, track, push])

  const handleClaimFreeTicketClick = useCallback(() => {
    track('Claim Free Ticket Clicked from theatrical PIF Page')
    if (theatricalRelease?.region?.ticketsStatus === 'COMING_SOON') return openModal('free-ticket')
    push(`${ticketUrl}?promo=claim-free-ticket`)
  }, [openModal, theatricalRelease?.region?.ticketsStatus, ticketUrl, track, push])

  return (
    <div className="grid gap-11">
      <Button
        className="mt-4 flex items-center justify-center gap-2 py-3 md:mx-auto md:w-full md:max-w-[250px]"
        onClick={() => {
          track('New PIF page PIF button clicked')
          togglePifSection && togglePifSection()
        }}
        variant="oxide"
      >
        <PayItForwardIcon color="white" />
        {t('payItForwardv2', 'Pay it Forward')}
      </Button>
      <div className="grid gap-4 text-center">
        <div className="grid gap-2">
          <TitleMD weight="bold">{t('purchaseTickets', 'Purchase Tickets')}</TitleMD>
          <ParagraphMD className="md:mx-auto md:max-w-xs" color="core-gray-700">
            {purchaseTicketDescription}
          </ParagraphMD>
        </div>
        {theatricalRelease?.region?.useSimpleWaitlistOverlay &&
        typeof theatricalRelease?.region?.partnerUrl === 'string' ? (
          <LinkButton
            className="flex items-center justify-center gap-2 py-3 md:mx-auto md:w-full md:max-w-[250px]"
            href={theatricalRelease.region.partnerUrl}
            variant="black"
            target="_blank"
            external
          >
            <TicketIcon color="white" />
            {t('getShowtimes', 'Get Showtimes')}
          </LinkButton>
        ) : (
          <Button
            className="flex items-center justify-center gap-2 py-3 md:mx-auto md:w-full md:max-w-[250px]"
            onClick={handleGetShowtimesClick}
            variant="black"
          >
            <TicketIcon color="white" />
            {t('getShowtimes', 'Get Showtimes')}
          </Button>
        )}
      </div>

      <div className="grid gap-4 text-center">
        <div className="grid gap-2">
          <TitleMD weight="bold">{t('cantAffordTickets', "Can't Afford Tickets?")}</TitleMD>
          <ParagraphMD className="md:mx-auto md:max-w-xs" color="core-gray-700">
            {t(
              'SofHelpCantAffordTicketsDescription',
              'Claim a free ticket from someone who paid it forward or share free tickets with someone.',
            )}
          </ParagraphMD>
        </div>
        <Button
          outline
          className="group flex items-center justify-center gap-2 py-3 md:mx-auto md:w-full md:max-w-[250px]"
          onClick={handleClaimFreeTicketClick}
          variant="black"
        >
          <TicketIcon className="group-hover:hidden" color="black" />
          <TicketIcon className="hidden group-hover:block" color="white" />
          {t('theatricalClaimFreeTicket', 'Claim Free Ticket')}
        </Button>
      </div>

      <TheatricalPifEmailCollectionModal
        closeModal={closeModal}
        isModalOpen={isModalOpen}
        modalType={modalType}
        modalState={modalState}
        setModalState={setModalState}
        trailer={theatricalRelease?.region?.trailerUrl as string}
      />
    </div>
  )
}
